import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedAccessToken && storedUser) {
      setAccessToken(storedAccessToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  /**
   * Refresh user profile from the server.
   * Pulls latest fullName, role, profileImageUrl, etc.
   * Call after uploading a profile image so Header/Sidebar update instantly.
   */
  const refreshProfile = useCallback(async () => {
    try {
      const data = await api.getProfile();
      setUser((prev) => {
        const next = {
          ...(prev || {}),
          fullName: data.fullName,
          email: data.email,
          role: data.role,
          companyName: data.companyName,
          timezone: data.timezone,
          profileImageUrl: data.profileImageUrl || null,
        };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
      return data;
    } catch (err) {
      console.error('refreshProfile failed:', err);
      return null;
    }
  }, []);

  // Auto-refresh profile whenever we become authenticated
  // (covers session-restore from localStorage, not just fresh login)
  useEffect(() => {
    if (isAuthenticated) {
      refreshProfile();
    }
  }, [isAuthenticated, refreshProfile]);

  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      const { accessToken, refreshToken, user } = response;

      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(user));

      setAccessToken(accessToken);
      setUser(user);
      setIsAuthenticated(true);

      // fetch full profile (has profileImageUrl)
      refreshProfile();

      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (registerData) => {
    try {
      const response = await api.register(registerData);
      const { accessToken, refreshToken, user } = response;

      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(user));

      setAccessToken(accessToken);
      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    accessToken,
    login,
    register,
    logout,
    refreshProfile,   // 👈 new
    setUser,          // 👈 handy if any component wants to patch user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}